<div class="bg-brand-surface min-h-screen flex flex-col">
  <app-guide-modal *ngIf="showGuideModal" [title]="'DASHBOARD.GUIDE_TITLE' | translate"
    [subtitle]="'DASHBOARD.GUIDE_SUBTITLE' | translate" [steps]="guideSteps"
    [primaryLabel]="'DASHBOARD.GUIDE_PRIMARY' | translate" [secondaryLabel]="'DASHBOARD.GUIDE_SECONDARY' | translate"
    (primary)="beginGuideFlow()" (closed)="closeGuideModal()"></app-guide-modal>

  <!-- Hero Section -->
  <section class="bg-brand-accent text-white">
    <div class="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-8 sm:py-10">
      <div class="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
        <!-- User Welcome -->
        <div class="flex items-center gap-3 sm:gap-4 text-white">
          <div
            class="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white text-brand-accent flex items-center justify-center text-xl sm:text-2xl font-semibold shadow">
            {{ getInitials() }}
          </div>
          <div>
            <h1 class="text-2xl sm:text-3xl font-semibold">{{ 'DASHBOARD.WELCOME' | translate }}</h1>
            <p class="text-sm text-white/80 tracking-wide">{{ displayName }}</p>
          </div>
        </div>

        <!-- Onboarding Guide -->
        <div *ngIf="shouldShowGuideSection" class="w-full md:max-w-xl md:ml-auto">
          <p class="text-xs uppercase tracking-[0.3em] text-white/70 mb-2">{{ 'DASHBOARD.FIRST_STEPS' | translate }}</p>
          <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div class="text-left">
              <h3 class="text-lg sm:text-xl font-semibold text-white">{{ 'DASHBOARD.ONBOARDING_TITLE' | translate }}</h3>
              <p class="text-sm text-white/80 mt-1">{{ 'DASHBOARD.PROGRESS' | translate: {current: completedSteps,
                total: onboardingProgress.total} }}</p>
            </div>
            <button type="button"
              class="inline-flex items-center justify-center px-4 py-2 bg-white text-brand-accent rounded-full text-sm font-semibold hover:bg-blue-50 transition whitespace-nowrap"
              (click)="startGuide()">
              {{ 'DASHBOARD.GUIDE_ME' | translate }}
            </button>
          </div>
          <div class="mt-3 h-2 rounded-full bg-white/20">
            <div class="h-full rounded-full bg-white transition-all duration-300"
              [style.width]="(completedSteps / onboardingProgress.total) * 100 + '%'"></div>
          </div>
        </div>

        <!-- Day Summary -->
        <div *ngIf="!shouldShowGuideSection" class="w-full md:max-w-xl md:ml-auto">
          <p class="text-xs uppercase tracking-[0.3em] text-white/70 mb-2">{{ 'DASHBOARD.DAY_SUMMARY' | translate }}</p>
          <div class="grid grid-cols-2 gap-2 sm:gap-3">
            <article *ngFor="let item of daySummaryItems"
              class="rounded-xl bg-white/15 px-3 sm:px-4 py-3 border border-white/20">
              <p class="text-xs text-white/80">{{ item.label | translate }}</p>
              <p class="text-xl sm:text-2xl font-semibold text-white mt-1">{{ item.value }}</p>
            </article>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- Main Content -->
  <section class="flex-1 bg-brand-surface">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-6 mt-4 sm:mt-8">
      <!-- Shortcuts Header -->
      <div class="mb-5">
        <h2 class="text-xl sm:text-2xl font-semibold text-brand-ink">{{ 'DASHBOARD.SHORTCUTS_TITLE' | translate }}</h2>
        <p class="text-sm text-brand-muted mt-1">
          {{ 'DASHBOARD.SHORTCUTS_DESC' | translate }}
        </p>
      </div>

      <!-- Shortcuts Grid -->
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <a *ngFor="let shortcut of homeShortcuts" [routerLink]="shortcut.path"
          class="rounded-2xl border p-4 sm:p-5 transition hover:shadow-md active:scale-[0.98]" [ngClass]="getShortcutCardClasses(shortcut.tone)">
          <div class="flex items-start justify-between gap-3">
            <div class="flex-1 min-w-0">
              <h3 class="text-base font-semibold text-brand-ink">{{ shortcut.title | translate }}</h3>
              <p class="text-sm text-brand-muted mt-2 line-clamp-2">{{ shortcut.description | translate }}</p>
            </div>
            <span
              class="shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-full bg-white/90 text-brand-accent">
              <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 12h14m-7-7 7 7-7 7" />
              </svg>
            </span>
          </div>
        </a>
      </div>

      <!-- Usage Summary -->
      <div class="mt-8">
        <div class="flex items-center justify-between mb-4">
          <div>
            <h2 class="text-lg sm:text-xl font-semibold text-brand-ink">{{ 'DASHBOARD.USAGE_TITLE' | translate }}</h2>
            <p class="text-sm text-brand-muted mt-0.5">{{ 'DASHBOARD.USAGE_SUBTITLE' | translate }}</p>
          </div>
          <span class="hidden sm:inline-block text-xs font-medium text-brand-muted bg-brand-surface-card border border-brand rounded-full px-3 py-1">
            {{ currentPeriod }}
          </span>
        </div>

        <!-- Error -->
        <div *ngIf="!usageLoading && usageError" class="rounded-2xl border border-red-200 bg-red-50 p-5 flex items-center justify-between gap-4">
          <p class="text-sm text-red-700">{{ 'DASHBOARD.USAGE_ERROR' | translate }}</p>
          <button type="button" (click)="retryUsage()"
            class="shrink-0 text-xs font-semibold text-red-700 border border-red-300 rounded-full px-3 py-1 hover:bg-red-100 transition">
            {{ 'DASHBOARD.USAGE_RETRY' | translate }}
          </button>
        </div>

        <!-- Meter Cards + empty note (single connected block) -->
        <div *ngIf="!usageLoading && !usageError">
          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            <article *ngFor="let config of meterConfigs"
              class="rounded-2xl border p-4 sm:p-5 transition hover:shadow-sm"
              [ngClass]="[config.bgClass, config.borderClass]">
              <div class="flex items-start justify-between gap-2 mb-3">
                <span class="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-white/80 shadow-sm" [ngClass]="config.colorClass">
                  <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.8">
                    <path stroke-linecap="round" stroke-linejoin="round" [attr.d]="config.iconPath" />
                  </svg>
                </span>
              </div>
              <p class="text-xs font-medium text-brand-muted mb-1">{{ config.labelKey | translate }}</p>
              <p class="text-2xl sm:text-3xl font-bold text-brand-ink">
                {{ (getUsageItem(config.meterCode)?.qtyTotal ?? 0) | number }}
              </p>
              <p class="text-xs text-brand-muted mt-1">{{ 'DASHBOARD.USAGE_SIGNATURES' | translate }}</p>
              <div class="mt-3 pt-3 border-t" [ngClass]="config.borderClass">
                <p class="text-sm font-semibold" [ngClass]="config.colorClass">
                  {{ formatCurrency(getUsageItem(config.meterCode)?.amountTotal ?? 0) }}
                </p>
              </div>
            </article>
          </div>

          <!-- Empty note attached directly under the cards -->
          <div *ngIf="usageSummary.length === 0"
            class="mt-3 flex items-center justify-center gap-2 rounded-2xl border border-dashed border-brand bg-brand-surface-alt/40 px-6 py-4 text-center text-sm text-brand-muted">
            <i class="pi pi-inbox text-brand-muted-light"></i>
            <span>{{ 'DASHBOARD.USAGE_EMPTY' | translate }}</span>
          </div>
        </div>

        <!-- Totals footer -->
        <div *ngIf="!usageLoading && !usageError && usageSummary.length > 0"
          class="mt-4 rounded-2xl bg-[#5d59f5]/6 border border-[#5d59f5]/20 px-5 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div class="flex items-center gap-6">
            <div>
              <p class="text-xs text-brand-muted">{{ 'DASHBOARD.USAGE_TOTAL_QTY' | translate }}</p>
              <p class="text-xl font-bold text-brand-ink">{{ usageTotalQty | number }}</p>
            </div>
            <div class="w-px h-8 bg-[#5d59f5]/20"></div>
            <div>
              <p class="text-xs text-brand-muted">{{ 'DASHBOARD.USAGE_TOTAL_AMOUNT' | translate }}</p>
              <p class="text-xl font-bold text-[#5d59f5]">{{ formatCurrency(usageTotalAmount) }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</div>
